/**
 * useUpdateChannel — the shell's view of the service-worker update channel
 * (sw/update-channel.ts): whether a newer build is waiting, the status line the
 * settings menu shows, the running bundle's name, and the check/apply actions
 * behind the update pill and the settings menu's buttons.
 *
 * Applying reloads the page, so it is refused while a capture is open
 * (`updateSafe` false). The refusal sets status "blocked" and calls `onBlocked`,
 * which the shell uses to open the settings menu so the reason is on screen.
 */

import { useEffect, useMemo, useState } from "preact/hooks";
import { applyUpdate, checkForUpdate, onUpdateReady, type UpdateCheckResult } from "../sw/update-channel";

export type UpdateStatus = UpdateCheckResult | "idle" | "checking" | "applying" | "blocked";

export interface UseUpdateChannel {
  updateReady: boolean;
  updateStatus: UpdateStatus;
  bundleName: string;
  handleUpdateCheck: () => Promise<void>;
  handleUpdateApply: () => Promise<void>;
}

export function useUpdateChannel(updateSafe: boolean, onBlocked: () => void): UseUpdateChannel {
  // A newer build is installed and waiting. Subscribing (rather than reading
  // once) matters because the SW usually finishes installing well after first
  // paint — the update lands mid-session and the pill appears then.
  const [updateReady, setUpdateReady] = useState(false);
  useEffect(() => onUpdateReady(setUpdateReady), []);
  const [updateStatus, setUpdateStatus] = useState<UpdateStatus>("idle");
  useEffect(() => {
    if (updateReady) setUpdateStatus((current) => current === "blocked" ? current : "ready");
  }, [updateReady]);

  const bundleName = useMemo(() => {
    if (typeof document === "undefined") return "unknown";
    const src = Array.from(document.scripts)
      .map((script) => script.src)
      .find((value) => /\/assets\/index-[^/]+\.js$/.test(value));
    return src ? new URL(src).pathname.split("/").pop() ?? "unknown" : "development";
  }, []);

  async function handleUpdateCheck() {
    setUpdateStatus("checking");
    setUpdateStatus(await checkForUpdate());
  }

  async function handleUpdateApply() {
    if (!updateSafe) {
      setUpdateStatus("blocked");
      onBlocked();
      return;
    }
    setUpdateStatus("applying");
    try {
      if (!(await applyUpdate())) setUpdateStatus(await checkForUpdate());
    } catch {
      setUpdateStatus("failed");
    }
  }

  return { updateReady, updateStatus, bundleName, handleUpdateCheck, handleUpdateApply };
}
